// ─────────────────────────────────────────────
//  AssignmentModal — "Add Assignment" from the OR board.
//  Same fields as the Daily Provider Assignment List grid: Start, End,
//  Surgeon, CRNA, Relief CRNA, MD/DO, Relief MD/DO. Saving upserts the
//  day's assignment row (the source of truth shared with the assignment
//  page) and mirrors the staff onto the live room doc so break coverage
//  and the provider's room actions keep working.
//  Selecting a room that already has an assignment row pre-fills the form.
//  Fires notifyAIC() best-effort when a supervising doctor saves a change
//  (D-11) — a notify failure never fails the save. Light token theme (D-13).
//
//  TWO SAVE PATHS (2026-08-17):
//    "Save Assignment"   — the original UPSERT. Unchanged.
//    "Save as a new case" — ALWAYS createAssignment, so the same team can be
//                           placed at a SECOND hour without losing the first.
//  Both paths then tell the row's CRNA + supervising doctor on their phones
//  through the task-directive lane (src/domain/tasks.js).
//
//  PRESET / FORCE-NEW MODE (2026-08-20): the Timeline chart opens THIS modal
//  when empty lane space is clicked — "create a case for room X starting at
//  time Y". `presetRoomId` / `presetStartTime` / `presetEndTime` seed the form
//  and survive a room change; `forceNewCase` makes the ONLY save path
//  createAssignment, so a click on blank space can never overwrite the room's
//  morning case. A third dialog for this would have been the bug, not the
//  feature (TimelineChartPage.js:747-750).
// ─────────────────────────────────────────────
import React, { useEffect, useMemo, useState } from "react";
import tokens from "../theme/tokens";
import Modal from "./Modal";
import { useToast } from "../context/ToastContext";
import {
  setRoomAssignment,
  createAssignment,
  updateAssignmentRow,
  createRoom,
  notifyAIC,
  listProcedureRefs,
  createTask,
} from "../firebase/firestore";
import { labelFor } from "../domain/locations";
import { assignmentForRoom } from "../domain/gridTemplate";
import { uidOf } from "../domain/channels";
import { buildManualTask } from "../domain/tasks";
import { hmToMinutes, to12h } from "../domain/timeline";
// ⚠️ THE SHIPPED DETECTOR, REACHED THROUGH ONE ADAPTER. `assignmentConflict`
// runs `doubleBookedProviders` over a hypothetical board and keys the seat with
// `crnaSeatKey` — nothing about "who is in two rooms" is decided in this file.
import { buildConflictBoard, seatConflict, pickerConflicts } from "../domain/assignmentConflict";
import { START_TIME_OPTIONS, END_TIME_OPTIONS } from "./assignmentRow.cells";
import TimeCombo from "./TimeCombo";
import { rosterDateKey } from "../hooks/useDailyRoster";
// ⚠️ IMPORT **AND** RE-EXPORT. Both are called BY THIS FILE further down, and a
// bare `export { x } from "..."` forwards the name to importers without creating
// a binding in this module's scope — the form that compiles, passes the suite,
// and then throws ReferenceError at runtime (RoomCard's smartName paid for this
// once already).
import { assignmentMessage, rowNeedsNotice } from "../domain/assignmentNotice";

export { assignmentMessage, rowNeedsNotice };

const { color, radius, font } = tokens;

const EMPTY_FORM = {
  startTime: "",
  endTime: "",
  surgeon: "",
  ahpId: "",
  ahpDepartTime: "",
  crnaReliefId: "",
  attendingId: "",
  callOrder: "",
  mdReliefId: "",
};

// ⚠️ `TimeCombo` CAN ONLY EMIT A VALUE THAT IS IN ITS OPTION LIST, and only
// marks a listed option as selected. A preset that landed off the 15-minute
// grid (an imported row reading "13:07", or a caller that forgot to snap) would
// show in the box with no row in the list to re-choose it, and the next
// blur-commit that matched nothing would leave the operator staring at a value
// they cannot reselect. Same fix the surgeon select already uses further down:
// carry the odd value as an extra "orphan" option so the field never lies.
export const withOrphanTime = (options, value) =>
  /^\d{2}:\d{2}$/.test(value || "") && !options.includes(value)
    ? [...options, value].sort()   // "HH:MM" sorts lexically = chronologically
    : options;

// Seed the form from an existing assignment row (or blank for none).
const formFrom = (a) => ({
  startTime: a?.startTime || "",
  endTime: a?.endTime || "",
  surgeon: a?.surgeon || "",
  ahpId: a?.ahpId || "",
  ahpDepartTime: a?.ahpDepartTime || "",
  crnaReliefId: a?.crnaReliefId || "",
  attendingId: a?.attendingId || "",
  callOrder: "",
  mdReliefId: a?.mdReliefId || "",
});

export default function AssignmentModal({
  onClose,
  rooms,
  locations,
  activeLocs,
  rosterEmpty,
  doctors = [],
  providers = [],
  staff = [],       // optional wider pool for the staff-id → auth-uid resolution
  assignments = [],
  facilityId,
  mustNotifyAIC,
  realUser,
  onCreateList,
  onSetCallOrder,   // (attendingId, callOrder) → persists the doctor's day call # on the roster
  presetProviderId, // when opened from the bench: pre-select this CRNA/AA; AIC picks room + supervisor
  presetAttendingId, // doctor-bench New Room: pre-select this MD/DO
  presetRoomId,      // timeline empty-lane click: the room whose lane was clicked
  presetStartTime,   // ...and the snapped "HH:MM" the click landed on
  presetEndTime,     // ...and its default end (start + DEFAULT_CASE_MIN)
  // ⚠️ AN EMPTY-SPACE CLICK MUST NEVER EDIT THE ROOM'S EXISTING CASE. With this
  // set, EVERY save goes down the createAssignment branch — see `handleSave`.
  forceNewCase = false,
  simplified = false, // New-Room flow: show only Room, Start, End, CRNA, MD/DO
  doctorMode = false, // doctor New Room: show only Room + MD/DO (MD preset)
}) {
  const toast = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [assignRoom, setAssignRoom] = useState(presetRoomId || "");
  const [newRoomName, setNewRoomName] = useState("");
  const [newRoomLoc, setNewRoomLoc] = useState("main_or");
  // The preset TIMES seed the form directly and deliberately do NOT read the
  // room's existing row: the hours belong to the click, not to the room.
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    ...(presetProviderId ? { ahpId: presetProviderId } : {}),
    ...(presetAttendingId ? { attendingId: presetAttendingId } : {}),
    ...(presetStartTime ? { startTime: presetStartTime } : {}),
    ...(presetEndTime ? { endTime: presetEndTime } : {}),
  });
  const [surgeons, setSurgeons] = useState([]);

  // Surgeon master list — the same procedure_refs bag the assignment grid uses.
  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    listProcedureRefs(facilityId)
      .then((r) => { if (!cancelled) setSurgeons(r.surgeons || []); })
      .catch((err) => console.error("Load surgeon refs failed:", err));
    return () => { cancelled = true; };
  }, [facilityId]);

  const set = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  // Picking a room pre-fills the form from its existing assignment row. When
  // opened from the bench, keep the preset CRNA selected — the AIC is placing
  // that specific person, so an empty room must not clear them.
  const pickRoom = (roomId) => {
    setAssignRoom(roomId);
    const roomName = rooms.find((r) => r.id === roomId)?.name || "";
    const base = formFrom(roomId === "__new__" ? null : assignmentForRoom(roomName, assignments));
    // A preset person ALWAYS wins — the AIC is placing THAT doctor/CRNA, even
    // into a room that already has someone (they're replacing them).
    //
    // ⚠️ AND SO DO THE PRESET TIMES. `base` above is `formFrom(the room's own
    // row)`, which OVERWRITES startTime/endTime with that room's hours — so a
    // timeline create that started at 13:00 and then swapped rooms would
    // silently become the new room's 07:00 case. When the caller supplied
    // times they belong to the CLICK, not to the room. The operator's own edit
    // (`f.startTime`) is preferred over the raw preset so changing the room
    // does not undo a time they have since corrected.
    setForm((f) => ({
      ...base,
      ...(presetProviderId ? { ahpId: presetProviderId } : {}),
      ...(presetAttendingId ? { attendingId: presetAttendingId } : {}),
      ...(presetStartTime ? { startTime: f.startTime || presetStartTime } : {}),
      ...(presetEndTime ? { endTime: f.endTime || presetEndTime } : {}),
    }));
  };

  const surgeonList = [...surgeons].sort((x, y) => x.localeCompare(y));
  const surgeonOrphan = form.surgeon && !surgeonList.includes(form.surgeon);

  // A room already has a CRNA if its room doc names a provider OR its assignment
  // row carries an ahpId (the sheet importer writes the row, not room.providerId).
  const roomHasCrna = (r) => {
    if (r.providerId) return true;
    const a = assignmentForRoom(r.name, assignments);
    return !!(a && a.ahpId);
  };
  // Placing an unassigned CRNA (the bench "New Room" flow) → only offer rooms
  // that are actually open (no CRNA yet). The general Add-Assignment flow still
  // lists every room so an existing assignment can be edited.
  const roomOptions = simplified ? rooms.filter((r) => !roomHasCrna(r)) : rooms;

  // AVAILABLE ROOMS FIRST, AND THE REST LABELLED WITH WHO IS IN THEM.
  // Asked for 2026-08-17: a flat list of 23 rooms gives no clue which are free,
  // which is the actual question when adding a room to the board.
  //
  // ⚠️ THE OCCUPIED ONES ARE NOT REMOVED, and that is deliberate. `handleSave`
  // UPSERTS: choosing a room that already has a row EDITS that row. Filtering
  // them out would quietly delete the only way to edit an assignment from here.
  // They are grouped and named instead, so the choice is informed rather than
  // hidden — and picking one now says out loud whose assignment is about to be
  // replaced, which it did not before.
  const byName = (a, b) => a.name.localeCompare(b.name, undefined, { numeric: true });
  const occupantOf = (r) => {
    const a = assignmentForRoom(r.name, assignments);
    return r.providerName || a?.ahpName || (r.providerId || a?.ahpId ? "someone" : "");
  };
  const sortedRooms = roomOptions.slice().sort(byName);
  const freeRooms = sortedRooms.filter((r) => !roomHasCrna(r));
  const takenRooms = sortedRooms.filter((r) => roomHasCrna(r));
  const selectedRoom = rooms.find((r) => r.id === assignRoom) || null;
  // ⚠️ NOT IN FORCE-NEW MODE. "Saving replaces that assignment" is the exact
  // opposite of what a forced create does, and a false reassurance about
  // destroying someone's row is worse than no note at all. The second-bar note
  // further down is the true one for that mode.
  const replacing =
    !forceNewCase && selectedRoom && roomHasCrna(selectedRoom) ? occupantOf(selectedRoom) : "";

  // The row the plain save would OVERWRITE. Its existence is the whole
  // condition for the second save path: with no row, a plain save already
  // creates one, and a second button would only add a choice with no meaning.
  const existingRow = selectedRoom ? assignmentForRoom(selectedRoom.name, assignments) : null;
  // Doctor New Room shows no time fields, and a case with no hours draws
  // nothing on the timeline — there is no "different time" to save it at.
  // The bench "New Room" flow (`simplified`) is placing ONE unassigned CRNA
  // into an open room; a second save path there is a choice with no question
  // behind it, so that flow stays exactly as it was.
  const wouldAddSecondBar = Boolean(existingRow?.id) && !doctorMode && !simplified;
  // In force-new mode the primary button IS the new-case path, so offering a
  // second control beside it would be two buttons doing the same thing — the
  // exact duplication the comment above rules out.
  const canSaveAsNewCase = wouldAddSecondBar && !forceNewCase;

  // ── ONE PERSON, TWO ROOMS ──────────────────────────────────────────────────
  //
  // THE POLICY: WARN AND ALLOW, AND SAY SO BEFORE THE PICK AS WELL AS ON SAVE.
  //
  // ⚠️ THIS IS `timelineCommit`'s RULE, NOT A NEW ONE. `commitDecision`
  // (timelineCommit.js:206) states it in as many words: a same-person, two-room
  // clash WARNS and is written; only a same-room collision refuses. The reason
  // given there is the board's whole purpose — it must be able to REPRESENT a
  // schedule that is wrong so somebody can see it and fix it. A modal that
  // refused would put the operator in a position the drag lets them into, and a
  // modal that stayed silent (what shipped) lets the board assert that two
  // rooms are staffed by one person with nothing on screen anywhere.
  //
  // ⚠️ AND THE PICKER MARKS, IT DOES NOT HIDE. The assignment GRID removes an
  // already-booked CRNA from other rows' dropdowns (assignmentRow.cells.js:48)
  // — right for filling a blank schedule up front, wrong here, where the same
  // modal is used mid-day to MOVE somebody. Hiding makes a legitimate action
  // impossible with no explanation; the person then does it off-board, which is
  // the one outcome worse than a double booking on it.
  const conflictBoard = useMemo(() => buildConflictBoard(assignments, rooms), [assignments, rooms]);
  // The room this form is about to write. "__new__" is a room that does not
  // exist yet: it matches no saved row, which is exactly right — it is a
  // different room from every one of them.
  const pendingRoomName =
    assignRoom === "__new__"
      ? newRoomName.trim() || "the new room"
      : selectedRoom?.name || "";
  // The row a PLAIN save would overwrite. Passed so re-timing a room's own case
  // does not report that room clashing with itself.
  const replacesRowId = forceNewCase ? null : existingRow?.id || null;
  const conflictCtx = {
    board: conflictBoard,
    roomId: assignRoom,
    roomName: pendingRoomName,
    startTime: form.startTime,
    endTime: form.endTime,
    replacesRowId,
  };
  // Every CRNA in the list, judged once per render of the pool — so the operator
  // reads "already in OR 1 · 7:30 AM to 11:00 AM" beside the name, before
  // choosing. Recomputed when the room or the hours change, because both move
  // the window the clash is measured in.
  const crnaConflicts = useMemo(
    () =>
      pickerConflicts({
        board: conflictBoard,
        providers,
        roomId: assignRoom,
        roomName: pendingRoomName,
        startTime: form.startTime,
        endTime: form.endTime,
        replacesRowId,
      }),
    [conflictBoard, providers, assignRoom, pendingRoomName, form.startTime, form.endTime, replacesRowId]
  );
  // The clash the CURRENT selection would create — the banner under the picker.
  const selectedConflict = form.ahpId ? crnaConflicts.get(form.ahpId) || null : null;

  const handleSave = async (mode = "upsert") => {
    // ⚠️ `forceNewCase` OVERRIDES THE MODE, not just the button wiring. The
    // caller's promise is that no save from this mount can reach
    // updateAssignmentRow; a guard on one onClick would be one refactor away
    // from silently overwriting a room's morning case.
    const newCase = mode === "new" || forceNewCase;
    if (!assignRoom) { toast.error("Select a room first."); return; }
    if (assignRoom === "__new__" && !newRoomName.trim()) {
      toast.error("Enter a name for the new room.");
      return;
    }
    // ⚠️ THE NEW-CASE GUARD, AND ONLY THE NEW-CASE PATH. A zero-length or
    // inverted case draws NOTHING on the timeline: it saves, it reports
    // success, and it is invisible from that moment on — there is no bar to
    // click and nothing to notice. The upsert path is deliberately left alone
    // (it is how a half-filled row gets edited, and hours are not always known
    // when the room is opened).
    if (newCase) {
      const startMin = hmToMinutes(form.startTime);
      const endMin = hmToMinutes(form.endTime);
      if (startMin == null || endMin == null) {
        toast.error("A new case needs both a start and an end time.");
        return;
      }
      if (endMin <= startMin) {
        toast.error("The new case must end after it starts.");
        return;
      }
    }
    setSubmitting(true);
    try {
      let roomId = assignRoom;
      let roomName = rooms.find((r) => r.id === assignRoom)?.name || "";

      if (assignRoom === "__new__") {
        const newRoomRef = await createRoom({
          facilityId: facilityId || "default",
          name: newRoomName.trim(),
          location: newRoomLoc,
        });
        roomId = newRoomRef.id;
        roomName = newRoomName.trim();
      }

      const attending = doctors.find((d) => d.id === form.attendingId);
      const ahp = providers.find((p) => p.id === form.ahpId);
      const crnaRelief = providers.find((p) => p.id === form.crnaReliefId);
      const mdRelief = doctors.find((d) => d.id === form.mdReliefId);

      // Upsert the day's assignment row — same shape the grid saves, so both
      // interfaces stay in sync on the same record.
      const existing = assignmentForRoom(roomName, assignments);

      // ⚠️ JUDGED BEFORE THE WRITE, REPORTED AFTER IT — and NEVER blocking. The
      // board as it stands is the only board we can compare against (the
      // `assignments` prop cannot have re-arrived yet), and the timeline drag
      // does exactly this: write, then `toast.warning` (useCaseCommit.js:244).
      // Refusing here would make this modal stricter than the drag about the
      // identical situation, which `timelineCommit.js:206` rules out.
      //
      // ⚠️ `newCase` CHANGES THE ANSWER. A second case does not replace the
      // room's existing row, so that row stays on the hypothetical board — a
      // person given a second case in a room they already hold is not a clash,
      // but a person given one in a room somebody else's hours cover still is.
      const clash = seatConflict({
        ...conflictCtx,
        ahpId: form.ahpId,
        ahpName: ahp?.name || "",
        replacesRowId: newCase ? null : existing?.id || null,
      });
      const data = {
        facilityId: facilityId || "default",
        date: rosterDateKey(),
        orRoom: existing?.orRoom || roomName.replace(/^OR\s+/i, ""),
        surgeon: (form.surgeon || "").trim(),
        startTime: form.startTime || "",
        endTime: form.endTime || "",
        attendingId: form.attendingId || null,
        attendingName: attending?.name || "",
        ahpId: form.ahpId || null,
        ahpName: ahp?.name || "",
        ahpShiftCode: ahp?.shiftCode ?? null,
        ahpDepartTime: form.ahpDepartTime || "",
        crnaReliefId: form.crnaReliefId || null,
        crnaReliefName: crnaRelief?.name || "",
        mdReliefId: form.mdReliefId || null,
        mdReliefName: mdRelief?.name || "",
        source: "manual",
      };
      // ⚠️ THE ONE BRANCH THAT MAKES A SECOND CASE POSSIBLE. `newCase` NEVER
      // reaches updateAssignmentRow: the point of the feature is that the
      // original row survives, so the two hours sit beside each other.
      if (!newCase && existing?.id) {
        await updateAssignmentRow(existing.id, data);
      } else {
        await createAssignment(data);
      }

      // Mirror the staff onto the live room doc so breaks and the provider's
      // room actions keep working off room.providerId.
      //
      // ⚠️ NOT ON THE NEW-CASE PATH. The room doc says who is in the room NOW
      // and when this case started; an additional case is a SECOND block, often
      // later in the day. Writing its start time onto the live room would
      // re-label the case currently running.
      const providerRole = ahp?.subRole ? ahp.subRole.toUpperCase() : ahp?.role || "";
      if (!newCase) {
        await setRoomAssignment(roomId, {
          providerId: form.ahpId || null,
          providerName: ahp?.name || "",
          providerRole,
          supervisingDoctorId: form.attendingId || null,
          supervisorName: attending?.name || "",
          scheduledStartTime: form.startTime || "",
        });
      }

      // Persist the supervising doctor's call number onto the day's roster
      // (same store the grid's call-order sub-select writes), so MD Call and
      // the call list reflect it. Best-effort — never fails the assignment.
      if (onSetCallOrder && form.attendingId && form.callOrder !== "") {
        try {
          await onSetCallOrder(form.attendingId, Number(form.callOrder));
        } catch (callErr) {
          console.error("Set call order failed:", callErr);
        }
      }

      // A supervising doctor's assignment change notifies the AIC. Best-effort
      // — a notify failure never fails the assignment write (D-11).
      if (mustNotifyAIC) {
        try {
          await notifyAIC({
            facilityId: facilityId || "default",
            actor: { uid: realUser?.uid, name: realUser?.name },
            summary:
              `${newCase ? "added an additional case for" : "changed the assignment for"} ${roomName || "a room"}` +
              (ahp?.name ? ` (provider: ${ahp.name})` : ""),
          });
        } catch (notifyErr) {
          console.error("Notify AIC failed:", notifyErr);
        }
      }

      // ── TELL THE PEOPLE ON THE ROW ─────────
      // The CRNA and the supervising doctor named on the row that was JUST
      // SAVED — not the people who were there before it.
      //
      // ⚠️ NOTHING BELOW MAY UNDO THE SAVE. The assignment is committed and is
      // already on every open board; rolling a clinical write back because a
      // message failed would be the worse outcome by far. The whole block is
      // wrapped, and its only visible effect is which sentence the success
      // toast carries.
      //
      // ⚠️ ONE WRITE PER SAVE. `sendNotice` is computed once, the write happens
      // once, and the buttons are disabled while `submitting` — so a double
      // click cannot buzz the same two phones twice for one assignment.
      const sendNotice = newCase || rowNeedsNotice(existing, data);
      let sentCount = 0;
      let noticeFailed = false;
      let unreachable = [];
      if (sendNotice) {
        // ⚠️ AUTH UIDS, NOT STAFF IDS. The two are indistinguishable strings:
        // a staff id writes cleanly, shows as sent, and rings no phone
        // (domain/tasks.js). `uidOf` returns null for pool-only staff and for
        // an unknown id — there is no phone to reach.
        const pool = [...(staff || []), ...(doctors || []), ...(providers || [])];
        const onRow = [
          { id: data.ahpId, name: data.ahpName || ahp?.name || "" },
          { id: data.attendingId, name: data.attendingName || attending?.name || "" },
        ]
          .filter((p) => p.id)
          .map((p) => ({ ...p, uid: uidOf(pool, p.id) }));
        const recipientIds = Array.from(new Set(onRow.filter((p) => p.uid).map((p) => p.uid)));
        // Skipped in the WRITE, named in the TOAST: an operator who believes
        // somebody was told when they were not will stop expecting them.
        unreachable = onRow.filter((p) => !p.uid).map((p) => p.name || "Someone on this row");
        // Same builder an operator-typed directive goes through, so this
        // validates identically and lands as kind "manual".
        const payload = buildManualTask({
          facilityId: facilityId || "default",
          dateKey: rosterDateKey(),
          senderId: realUser?.uid,
          senderName: realUser?.name || "",
          recipientIds,
          text: assignmentMessage({
            roomName,
            startTime: data.startTime,
            endTime: data.endTime,
          }),
          roomName: roomName || null,
        });
        // A null payload = nobody reachable (or no sender): nothing to send,
        // and not a failure to report as one.
        if (payload) {
          try {
            await createTask(payload);
            sentCount = payload.recipientIds.length;
          } catch (taskErr) {
            console.error("Assignment directive failed:", taskErr);
            noticeFailed = true;
          }
        }
      }

      // Silent to the supervisor — the AIC receives the notification.
      const said = [`${newCase ? "New case saved" : "Assignment saved"}${roomName ? ` for ${roomName}` : ""}.`];
      if (sentCount > 0) said.push(`Message sent to ${sentCount}.`);
      if (noticeFailed) said.push("The message could not be sent.");
      if (unreachable.length > 0) {
        // "they", not "he"/"she": staff records carry no gender, and guessing
        // one in a message about a named colleague is not a small thing.
        said.push(
          `${unreachable.join(" and ")} ${unreachable.length > 1 ? "have" : "has"} no app account, so they were not messaged.`
        );
      }
      toast.success(said.join(" "));
      // ⚠️ A SEPARATE, AMBER TOAST — not a sentence appended to the green one.
      // "Assignment saved… and one person is now in two rooms" reads as part of
      // the confirmation and is skimmed past. Pushed AFTER the success so it is
      // the message left on screen (warnings also live 6s to success's 4s —
      // ToastContext.js:41), and it names the other room and the overlapping
      // hours the way the drag's warning does.
      if (clash) toast.warning(clash.message);
      onClose();
    } catch (err) {
      console.error("Failed to create assignment:", err);
      toast.error("Could not save the assignment. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Rendered through the shared accessible Modal (dialog role, focus trap,
  // Escape, focus restore) — the old hand-rolled overlay had none of those.
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={forceNewCase ? "＋ New Case" : "＋ Add Assignment"}
      maxWidth={440}
      footer={
        <>
          <button style={styles.outlineBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          {/* THE SECOND SAVE PATH — only where it means something. A room with
              no row already gets a new row from the plain save, so offering
              both there would be two buttons that do the same thing. */}
          {canSaveAsNewCase && (
            <button
              style={styles.outlineBtn}
              disabled={submitting}
              onClick={() => handleSave("new")}
            >
              {submitting ? "Saving…" : "Save as a new case"}
            </button>
          )}
          {/* ⚠️ NAMED FOR THE CONSEQUENCE. In force-new mode this button adds a
              case; it never edits one, and calling it "Save Assignment" would
              be the same verb for the opposite outcome. */}
          <button
            style={styles.btnPrimary}
            disabled={submitting}
            onClick={() => handleSave(forceNewCase ? "new" : "upsert")}
          >
            {submitting ? "Saving…" : forceNewCase ? "Create case" : "Save Assignment"}
          </button>
        </>
      }
    >
        <div style={styles.modalSub}>
          Same fields as the Daily Provider Assignment List — saving keeps both
          views in sync.
        </div>

        {rosterEmpty && (
          <div style={styles.rosterHint}>
            No one is on today's Daily List yet.{" "}
            <button style={styles.linkBtn} onClick={onCreateList}>
              Create today's list
            </button>{" "}
            to choose the AIC, doctors, and CRNAs who can be assigned today.
          </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.label} htmlFor="am-room">Room</label>
          <select
            id="am-room"
            style={styles.select}
            value={assignRoom}
            onChange={(e) => pickRoom(e.target.value)}
          >
            <option value="">Select a room...</option>
            {/* Native optgroups rather than a custom listbox: this is a picker
                used a handful of times a day, and a native select is
                keyboard-operable and screen-reader-labelled without being
                taught. */}
            {freeRooms.length > 0 && (
              <optgroup label="Available">
                {freeRooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} — {labelFor(locations, r.location)}
                  </option>
                ))}
              </optgroup>
            )}
            {takenRooms.length > 0 && (
              <optgroup
                label={
                  forceNewCase
                    ? "Already staffed — a new case is added beside the existing one"
                    : "Already staffed — choosing one replaces its assignment"
                }
              >
                {takenRooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name} — {labelFor(locations, r.location)}
                    {occupantOf(r) ? ` · ${occupantOf(r)}` : ""}
                  </option>
                ))}
              </optgroup>
            )}
            {simplified && roomOptions.length === 0 && (
              <option value="" disabled>No open rooms — every room has a CRNA.</option>
            )}
            <option value="__new__">＋ Add a new room...</option>
          </select>
          {/* Saying it once more at the point of no return. The upsert is silent
              by nature — the form looks identical whether it is creating a row
              or overwriting somebody's. */}
          {replacing && (
            <p style={styles.replaceNote} role="status">
              {selectedRoom.name} already has {replacing}. Saving replaces that assignment.
            </p>
          )}
        </div>

        {assignRoom === "__new__" && (
          <>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="am-new-room-name">New Room Name</label>
              <input
                id="am-new-room-name"
                style={styles.input}
                placeholder="e.g. OR 13, Hybrid OR, Endo 4"
                value={newRoomName}
                onChange={(e) => setNewRoomName(e.target.value)}
              />
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label} htmlFor="am-new-room-loc">Location</label>
              <select
                id="am-new-room-loc"
                style={styles.select}
                value={newRoomLoc}
                onChange={(e) => setNewRoomLoc(e.target.value)}
              >
                {activeLocs.map((o) => (
                  <option key={o.key} value={o.key}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {!doctorMode && (
        <div style={styles.timeRow}>
          <div style={{ ...styles.formGroup, flex: 1 }}>
            <label style={styles.label} htmlFor="assign-start-time">Start Time</label>
            <TimeCombo
              id="assign-start-time"
              ariaLabel="Start time"
              value={form.startTime}
              options={withOrphanTime(START_TIME_OPTIONS, form.startTime)}
              onChange={(v) => setForm((f) => ({ ...f, startTime: v }))}
            />
          </div>
          <div style={{ ...styles.formGroup, flex: 1 }}>
            <label style={styles.label} htmlFor="assign-end-time">End Time</label>
            <TimeCombo
              id="assign-end-time"
              ariaLabel="End time"
              value={form.endTime}
              options={withOrphanTime(END_TIME_OPTIONS, form.endTime)}
              onChange={(v) => setForm((f) => ({ ...f, endTime: v }))}
            />
          </div>
        </div>
        )}

        {!simplified && !doctorMode && (
        <div style={styles.formGroup}>
          <label style={styles.label} htmlFor="am-surgeon">Surgeon</label>
          <select id="am-surgeon" style={styles.select} value={form.surgeon} onChange={set("surgeon")}>
            <option value="">Pick a surgeon…</option>
            {surgeonOrphan && <option value={form.surgeon}>{form.surgeon}</option>}
            {surgeonList.map((sName) => <option key={sName} value={sName}>{sName}</option>)}
          </select>
        </div>
        )}

        {!doctorMode && (
        <div style={styles.formGroup}>
          <label style={styles.label} htmlFor="am-crna">CRNA</label>
          {/* `aria-describedby` points at the clash banner below, so a screen
              reader reaching this field hears WHY an option is marked rather
              than only meeting the suffix mid-list. */}
          <select
            id="am-crna"
            style={styles.select}
            value={form.ahpId}
            onChange={set("ahpId")}
            aria-describedby={selectedConflict ? "am-crna-clash" : undefined}
          >
            <option value="">Pick a CRNA/AA…</option>
            {providers.map((p) => {
              // ⚠️ TEXT, NOT COLOUR, AND STILL SELECTABLE. A native <option>
              // cannot be reliably styled across browsers and a colour-only
              // marker fails WCAG 1.4.1 anyway — but more to the point, this
              // has to survive being read aloud. The suffix names the room and
              // the hours, so the clash is knowable BEFORE the pick.
              const c = crnaConflicts.get(p.id);
              return (
                <option key={p.id} value={p.id}>
                  {p.name} · {(p.subRole || "crna").toUpperCase()}
                  {c ? ` — ${c.note}` : ""}
                </option>
              );
            })}
          </select>
          {/* Said again in full once they are chosen: the collapsed <select>
              shows the suffix, but it is long, and the operator has usually
              stopped reading the picker by the time they reach Save.
              ⚠️ role="status" (polite), NOT alert — this is a warning about a
              schedule, and an assertive interruption on every keystroke of the
              time fields would make the field unusable. Amber, never red: red on
              this board means an emergency. */}
          {selectedConflict && (
            <p id="am-crna-clash" style={styles.replaceNote} role="status" data-testid="crna-clash">
              {selectedConflict.message} Saving is still allowed — the board has
              to be able to show a schedule that is wrong.
            </p>
          )}
        </div>
        )}

        <div style={styles.formGroup}>
          <label style={styles.label} htmlFor="am-mddo">MD/DO</label>
          <select id="am-mddo" style={styles.select} value={form.attendingId} onChange={set("attendingId")}>
            <option value="">Pick a doctor…</option>
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>
                {(d.shortCode ? `${d.shortCode} — ` : "") + d.name}
              </option>
            ))}
          </select>
        </div>

        {/* ⚠️ THE LIMITATION, SAID OUT LOUD, BESIDE THE BUTTON THAT CAUSES IT.
            The timeline iterates ASSIGNMENTS (domain/timeline.js ~429), so a
            second row for a room renders as a second bar — but
            `assignmentForRoom` returns list.find(...), the FIRST match, and the
            Daily List grid and the room modal both read through it. So the
            extra case is real and visible on the chart while those two surfaces
            keep showing the original row. That is the current data model, not
            something this modal can fix, and an operator who learns it from the
            grid instead of from here will assume the save failed.
            Amber, never red: red on this board means an emergency. */}
        {/* Keyed to the OUTCOME, not to the button: force-new mode hides the
            "Save as a new case" control but still adds the second bar, so the
            limitation is exactly as true there. */}
        {wouldAddSecondBar && (
          <p style={styles.replaceNote} data-testid="new-case-limit">
            Saving as a new case adds a second bar for {selectedRoom.name} on the
            timeline, but the Daily List grid and the room card keep showing only
            its original row.
          </p>
        )}

    </Modal>
  );
}

const styles = {
  // Overlay/panel/title/actions now come from the shared Modal primitive.
  modalSub: { fontSize: 13, color: color.text.muted, marginBottom: 20 },
  // Amber, not red: replacing an assignment is a normal edit that the operator
  // must simply be AWARE of. Red on this board means an emergency.
  replaceNote: {
    margin: "8px 0 0",
    fontSize: 12.5,
    lineHeight: 1.4,
    color: "#7a4f00",
    background: "#fef8ec",
    border: "1px solid #f59e0b",
    borderRadius: radius.sm,
    padding: "7px 9px",
  },
  formGroup: { marginBottom: 16 },
  timeRow: { display: "flex", gap: 12 },
  label: {
    display: "block",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    marginBottom: 6,
  },
  input: {
    width: "100%",
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.sm,
    padding: "9px 12px",
    fontSize: 13,
    boxSizing: "border-box",
  },
  select: {
    width: "100%",
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    color: color.text.primary,
    borderRadius: radius.sm,
    padding: "9px 12px",
    fontSize: 13,
  },
  // 44px-min footer buttons (WCAG 2.5.8 / mobile touch target).
  outlineBtn: {
    background: "transparent",
    border: `1px solid ${color.border.default}`,
    color: color.text.secondary,
    borderRadius: radius.sm,
    padding: "10px 18px",
    minHeight: 44,
    fontSize: 13,
    cursor: "pointer",
  },
  btnPrimary: {
    background: color.accent.primary,
    color: "#fff",
    border: "none",
    borderRadius: radius.sm,
    padding: "10px 18px",
    minHeight: 44,
    fontSize: font.size.body,
    fontWeight: font.weight.bold,
    cursor: "pointer",
  },
  rosterHint: {
    background: "#f59e0b12",
    border: "1px solid #f59e0b44",
    borderRadius: radius.md,
    padding: "10px 12px",
    marginBottom: 16,
    fontSize: font.size.label,
    color: "#7a4f00",
    lineHeight: 1.5,
  },
  linkBtn: {
    background: "transparent",
    border: "none",
    color: color.accent.primary,
    cursor: "pointer",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    padding: 0,
    textDecoration: "underline",
  },
};
